import { useId, useState, type ReactNode } from "react";
import { Mail, Pencil, Phone, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation } from "@apollo/client/react";
import { Button, Card, Eyebrow, Text } from "@athena/ui";
import { ClientDocument, UpdateClientContactDetailsDocument } from "@/graphql/clients.generated";
import type { ClientRecord } from "../helpers/client";
import { ContactDetailsDialog, type ContactDetails, type ContactField } from "./ContactDetailsDialog";

/** What the success toast says happened. */
type SaveOutcome = "saved" | "emailRemoved" | "phoneRemoved";

/**
 * The other email and phone on an alum's account (ATH-244), which a coordinator adds, changes or
 * removes here. Emails meant for the alum go to that email while one is set; they still sign in
 * with their own.
 */
export function ContactDetailsCard({ client }: { client: ClientRecord }) {
    const { t } = useTranslation();
    const titleId = useId();
    const [
        editing,
        setEditing,
    ] = useState<ContactField[]>([]);

    // The profile is re-read: the header's email and phone are these when they are set.
    const [updateContactDetails] = useMutation(UpdateClientContactDetailsDocument, {
        refetchQueries: [ClientDocument],
        awaitRefetchQueries: true,
    });

    const { account, firstName } = client;
    const header = (
        <div className="flex items-baseline justify-between gap-2">
            <Eyebrow id={titleId}>{t("admin.alumni.profile.contactDetails.title")}</Eyebrow>
            <Text size="xs" muted>
                {t("admin.alumni.profile.contactDetails.optional")}
            </Text>
        </div>
    );
    if (!account) {
        return (
            <Card width="full" className="p-5" role="region" aria-labelledby={titleId}>
                {header}
                <Text size="sm" muted className="mt-3">
                    {t("admin.alumni.profile.contactDetails.noAccount", { firstName })}
                </Text>
            </Card>
        );
    }

    const current: ContactDetails = {
        contactEmail: account.contactEmail ?? null,
        contactPhone: account.contactPhone ?? null,
    };

    /** Saves `changed` over what is there. Rejects when the save fails, after saying so. */
    const save = async (changed: Partial<ContactDetails>, outcome: SaveOutcome) => {
        const details = { ...current, ...changed };
        try {
            await updateContactDetails({
                variables: {
                    userId: account.userId,
                    contactEmail: details.contactEmail,
                    contactPhoneNumber: details.contactPhone,
                },
            });
        } catch (error) {
            toast.error(t("admin.alumni.profile.contactDetails.saveFailed", { firstName }));
            throw error;
        }
        toast.success(t(`admin.alumni.profile.contactDetails.${outcome}`, { firstName }));
    };
    // The failure was already said; there is nothing else to undo.
    const remove = (field: ContactField, outcome: SaveOutcome) =>
        save({ [field]: null }, outcome).catch(() => undefined);

    const missing = (Object.keys(current) as ContactField[]).filter((field) => !current[field]);

    return (
        <Card width="full" className="p-5" role="region" aria-labelledby={titleId}>
            {header}
            {account.contactEmail || account.contactPhone ? (
                <ul className="mt-3">
                    {account.contactEmail ? (
                        <ContactRow
                            icon={<Mail className="h-4 w-4" />}
                            value={account.contactEmail}
                            detail={t("admin.alumni.profile.contactDetails.emailDetail", {
                                email: account.signInEmail,
                            })}
                            editLabel={t("admin.alumni.profile.contactDetails.editEmail")}
                            removeLabel={t("admin.alumni.profile.contactDetails.removeEmail")}
                            onEdit={() => setEditing(["contactEmail"])}
                            onRemove={() => remove("contactEmail", "emailRemoved")}
                        />
                    ) : null}
                    {account.contactPhone ? (
                        <ContactRow
                            icon={<Phone className="h-4 w-4" />}
                            value={account.contactPhone}
                            editLabel={t("admin.alumni.profile.contactDetails.editPhone")}
                            removeLabel={t("admin.alumni.profile.contactDetails.removePhone")}
                            onEdit={() => setEditing(["contactPhone"])}
                            onRemove={() => remove("contactPhone", "phoneRemoved")}
                        />
                    ) : null}
                </ul>
            ) : (
                <Text size="sm" muted className="mt-3">
                    {t("admin.alumni.profile.contactDetails.none", { email: account.signInEmail })}
                </Text>
            )}
            {missing.length > 0 ? (
                <Button
                    variant="link"
                    className="mt-3 w-auto px-0 tracking-normal normal-case"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => setEditing(missing)}
                >
                    {t("admin.alumni.profile.contactDetails.add")}
                </Button>
            ) : null}
            <ContactDetailsDialog
                fields={editing}
                onClose={() => setEditing([])}
                firstName={firstName}
                account={account}
                onSave={(entered) => save(entered, "saved")}
            />
        </Card>
    );
}

function ContactRow({
    icon,
    value,
    detail,
    editLabel,
    removeLabel,
    onEdit,
    onRemove,
}: {
    icon: ReactNode;
    value: string;
    detail?: string;
    editLabel: string;
    removeLabel: string;
    onEdit: () => void;
    onRemove: () => void;
}) {
    return (
        <li className="border-border flex items-center gap-3 border-t py-3">
            <span
                className="bg-muted text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                aria-hidden
            >
                {icon}
            </span>
            <div className="min-w-0 flex-1">
                <Text size="sm" className="text-text-heading font-mono break-all">
                    {value}
                </Text>
                {detail ? (
                    <Text size="xs" muted className="mt-0.5">
                        {detail}
                    </Text>
                ) : null}
            </div>
            <div className="flex shrink-0 gap-1">
                <Button variant="ghost" size="icon-sm" aria-label={editLabel} onClick={onEdit}>
                    <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon-sm" aria-label={removeLabel} onClick={onRemove}>
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>
        </li>
    );
}
